"use client"

import { useState } from "react"
import Image from "next/image"
import { useSelectedLayoutSegment } from "next/navigation"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { Button, Separator, SidebarTrigger } from "@ui/shad"
import { ArrowUpCircle } from "lucide-react"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import { UpgradeDialog } from "@/components/billing/upgrade-dialog"
import { BulkEmailStatusPopover } from "@/components/bulk-email"
import { ChatLauncher } from "@/components/chat/chat-launcher"
import { BackfillStatusBadge } from "@/components/email-backfill"
import { IntakeFormNeedsReviewPopover } from "@/components/intake-forms/needs-review-popover"
import { KeyboardShortcutHelper } from "@/components/keyboard/shortcut-helper"
import { DataImportStatusPopover } from "@/components/settings/data-import-v2/status-popover"

import Breadcrumbs from "./breadcrumb"
import { PrepMeetingButton } from "./prep-meeting-button"

export function Header() {
  const segment = useSelectedLayoutSegment()
  const { data: currentUser } = useFetchCurrentUser()
  const bookId = useBookId()
  const router = useLoadingRouter()
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)
  const isDialogOpen = useGlobalDialogStore((state) => state.isOpen)
  const hideHeader = ["testroute"].includes(segment || "")

  if (hideHeader) return null

  return (
    <header className="flex h-12 w-full max-w-full flex-none items-center justify-between gap-2 border-b pr-2 pl-4">
      <div className="flex grow items-center gap-2">
        <SidebarTrigger className="text-icon -ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumbs />
      </div>
      <div className="flex items-center gap-4">
        <BackfillStatusBadge />
        {currentUser?.company?.solo_trial && (
          <>
            <Button variant="brand" size="sm" className="shrink-0" onClick={() => setUpgradeDialogOpen(true)}>
              <ArrowUpCircle />
              Upgrade
            </Button>
            <UpgradeDialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen} />
          </>
        )}
        <PrepMeetingButton />
        <div className="flex items-center">
          {bookId && !isAllBooksId(bookId) && (
            <Button
              variant="ghost"
              size="iconSm"
              aria-label="Slanta Claus"
              tooltip="Slanta Claus"
              disabled={isDialogOpen}
              onClick={() => router.push("/slanta")}
            >
              <Image
                src="/slanta/slant-santa-hat.png"
                alt=""
                width={24}
                height={24}
                className="size-6! object-contain"
              />
            </Button>
          )}
          <IntakeFormNeedsReviewPopover />
          <BulkEmailStatusPopover />
          <DataImportStatusPopover />
          <ChatLauncher />
          <KeyboardShortcutHelper />
        </div>
      </div>
    </header>
  )
}
